import {
  Box,
  Button,
  Heading,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Stack,
  Text,
  VStack,
  HStack,
} from "@chakra-ui/react";
import { FormProvider, SubmitHandler, useForm } from "react-hook-form";
import { AdminTicketType, KYDTicketType } from "@common/interfaces/KYDTicket";
import { EventDetailTicketTypesSection } from "@common/interfaces/KYDEvent";
import TicketTypeButtons from "./TieredTicketType/TicketTypeButtons";
import TicketTypeTimeline from "./TieredTicketType/TicketTypeTimeline";
import TieredAllocation from "./TieredTicketType/PriceMode/TieredAllocation";
import SingleAllocation from "./TieredTicketType/PriceMode/SingleAllocation";
import InputWithLabel from "./TieredTicketType/InputWithLabel";
import { yupResolver } from "@hookform/resolvers/yup";
import {
  Mode,
  PriceMode,
  TicketTypeTier,
  TicketTypeValues,
  getTicketTypeValuesSchema,
  transform,
} from "./TieredTicketType/schema";
import TicketTypeSettingsSection from "./TieredTicketType/TicketTypeSettingsSection";
import { useMemo } from "react";
import { extractDirtyProperties } from "./TieredTicketType/util";

const PriceComponents: Record<
  PriceMode,
  (props: {
    currency: string;
    loading: boolean;
    timezone: string;
    eventId: string;
    onRemoveTier: (tier: TicketTypeTier) => Promise<void>;
  }) => JSX.Element
> = {
  single: SingleAllocation,
  tiered_allocation: TieredAllocation,
};

const EditTicketTypeModal = ({
  onClose,
  onDelete = async (_ticket_type_id: string) => {},
  mode,
  ticketType,
  data,
  link,
  currency,
  isLoading,
  timezone,
  onSubmit,
  onRemoveTier,
}: {
  onClose: () => void;
  mode: Mode;
  ticketType?: AdminTicketType;
  timezone: string;
  link: string;
  currency: string;
  data: EventDetailTicketTypesSection["data"];
  isLoading: boolean;
  onSubmit: (data: Partial<TicketTypeValues>) => void;
  onDelete?: (ticketTypeId: string) => Promise<void>;
  onRemoveTier: (tier: TicketTypeTier) => Promise<void>;
}) => {
  const transformed = useMemo(
    () =>
      transform.input(
        ticketType,
        {
          tiered_ticket_types: data.tiered_ticket_types,
          timezone,
        },
        data.event_defaults
      ),
    [ticketType, data]
  );

  const methods = useForm<TicketTypeValues>({
    resolver: yupResolver(
      getTicketTypeValuesSchema({
        end_at: data.end_at,
        start_at: data.start_at,
        timezone,
        off_sale_at: data.off_sale_at,
        on_sale_at: data.on_sale_at,
      })
    ),
    criteriaMode: "all",
    mode: "all",
    reValidateMode: "onChange",
    defaultValues: transformed,
  });

  const {
    watch,
    handleSubmit,
    setValue,
    trigger,
    // crazy bug: https://github.com/react-hook-form/react-hook-form/issues/1146
    formState: {
      defaultValues,
      dirtyFields,
      isValid,
      isDirty,
      touchedFields,
      errors,
    },
    clearErrors,
  } = methods;

  const isEdit = mode === "edit";
  const priceMode = watch("price_mode");
  const waitlistStatus = watch("waitlist_status");
  const waitlistReturn = watch("waitlist_return");
  const canBeDeleted =
    ticketType?.display_options?.delete_options?.includes("DELETE");

  const _onSubmit: SubmitHandler<TicketTypeValues> = (data) => {
    const hasErrors = Object.keys(errors).length > 0;
    if (hasErrors) {
      console.log({ input_errors: errors });
    }

    console.log({
      data,
      dirtyFields,
    });

    trigger();
    console.log({
      formState: {
        mode: "after",
        def: defaultValues,
        dirty: dirtyFields,
        isDirty: isDirty,
        touched: touchedFields,
      },
      data,
      transformed: transform.output(
        {
          id: ticketType?.id,
          ...extractDirtyProperties(data, dirtyFields),
        },
        "edit",
        timezone
      ),
    });

    if (isEdit) {
      return onSubmit(
        transform.output(
          {
            id: ticketType?.id,
            ...extractDirtyProperties(data, dirtyFields),
          },
          "edit",
          timezone
        )
      );
    }

    return onSubmit(transform.output(data, "create"));
  };

  const PriceComponentToRender = PriceComponents[priceMode];
  if (!PriceComponentToRender) {
    throw new Error(`No price component for ${priceMode}`);
  }

  const hasErrors = Object.keys(errors).length > 0;

  return (
    <Modal
      size="3xl"
      isOpen
      onClose={onClose}
      closeOnOverlayClick={false}
      closeOnEsc
    >
      <ModalOverlay />
      <ModalContent bg="#FAFAFA">
        <ModalHeader>
          <HStack
            w="full"
            justifyContent={"space-between"}
            alignItems={"center"}
          >
            <Text fontSize={"xl"} fontWeight={"bold"}>
              {isEdit ? "Edit Ticket Type" : "Create Ticket Type"}
            </Text>
            {!isEdit ? (
              <Box>
                <TicketTypeButtons
                  loading={isLoading}
                  priceMode={priceMode}
                  onSelect={(mode) => {
                    clearErrors();
                    setValue("price_mode", mode);
                  }}
                />
              </Box>
            ) : null}
          </HStack>
          {/* Dirty {`${formState.isDirty}`} */}
        </ModalHeader>
        <ModalBody>
          <FormProvider {...methods}>
            <VStack spacing={2}>
              <VStack spacing={6} w="full">
                {priceMode === PriceMode.SINGLE ? (
                  <Stack w="100%" spacing={0}>
                    <InputWithLabel
                      label="Name"
                      placeholder="Ticket Type name"
                      name="name"
                      inputProps={{
                        disabled: isLoading,
                      }}
                    />
                    <InputWithLabel
                      name="description"
                      label="Description"
                      placeholder="Ticket Type description"
                      inputProps={{
                        disabled: isLoading,
                      }}
                    />
                  </Stack>
                ) : null}
                {priceMode === PriceMode.TIERED_ALLOCATION ? (
                  <InputWithLabel
                    name="description"
                    label="Description"
                    placeholder="Ticket Type description"
                    inputProps={{
                      disabled: isLoading,
                    }}
                  />
                ) : null}

                <VStack w="full" spacing={1}>
                  <Heading
                    fontSize={"xl"}
                    fontWeight={"semibold"}
                    color="#1A202C"
                    w="full"
                  >
                    Pricing
                  </Heading>
                  <Box
                    w="full"
                    bg="white"
                    border="1px solid #E8E8E8"
                    borderRadius="16px"
                    padding="24px"
                  >
                    <PriceComponentToRender
                      eventId={data.id}
                      currency={currency}
                      loading={isLoading}
                      timezone={timezone}
                      onRemoveTier={onRemoveTier}
                    />
                  </Box>
                </VStack>
                {priceMode === PriceMode.SINGLE ? (
                  <TicketTypeTimeline timezone={timezone} loading={isLoading} />
                ) : null}
                <TicketTypeSettingsSection
                  isLoading={isLoading}
                  waitlistStatus={
                    waitlistStatus as KYDTicketType["waitlist_status"]
                  }
                  waitlistReturn={
                    waitlistReturn as KYDTicketType["waitlist_return"]
                  }
                />
              </VStack>
            </VStack>
          </FormProvider>
          <Box h={4}>
            {errors[`tiers`]?.message || errors[`tiers`]?.root?.message ? (
              <Text fontSize={"sm"} color="red.300">
                {`${errors["tiers"].message || errors["tiers"].root?.message}`}
              </Text>
            ) : null}
          </Box>
        </ModalBody>
        <ModalFooter>
          {canBeDeleted ? (
            <Button
              onClick={() => onDelete(ticketType.id)}
              isDisabled={isLoading}
              isLoading={isLoading}
              mr="4"
              bg="red.500"
              color="white"
            >
              Delete
            </Button>
          ) : null}
          <Button mr="4" isDisabled={isLoading} onClick={onClose}>
            Cancel
          </Button>
          {isEdit ? (
            <Button
              bg="green.500"
              color="white"
              isDisabled={!isDirty || isLoading || hasErrors}
              isLoading={isLoading}
              onClick={handleSubmit(_onSubmit, (errors) => {
                console.log({ errors });
              })}
            >
              Save
            </Button>
          ) : (
            <Button
              bg="green.500"
              // isDisabled={isLoading}
              isLoading={isLoading}
              color="white"
              onClick={handleSubmit(_onSubmit, (errors) => {
                console.log({ errors });
              })}
            >
              Create
            </Button>
          )}
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default EditTicketTypeModal;
